import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import {
  epelsaConfigService,
  epelsaTicketsLogService,
  epelsaKpisService,
  epelsaTicketProcessor,
} from '../../../services/epelsaService';

const ACCENT = '#34D399';
const GOLD = '#D6EF4E';

const fmt = (n, d = 2) => typeof n === 'number' ? n?.toLocaleString('es-ES', { minimumFractionDigits: d, maximumFractionDigits: d }) : '—';
const fmtEur = (n) => typeof n === 'number' ? `${fmt(n)} €` : '—';
const fmtDate = (d) => d ? new Date(d)?.toLocaleString('es-ES', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—';

const TICKET_DEMO = {
  numero_ticket: 'EPELSA-000001',
  fecha: new Date()?.toISOString(),
  lineas: [
    { plu: '001', ean: '8430000000000', nombre: 'Jamón Ibérico Cebo 50%', peso_kg: 0.438, precio_kg: 39.90, cantidad: 0.438, importe: 17.48, iva: 10 },
  ],
  forma_pago: 'EFECTIVO',
  vendedor: 'Mario Bernal',
  caja: 'EPELSA-01',
  total: 17.48,
};

const EpelsaBascula = () => {
  const [config, setConfig] = useState(null);
  const [kpis, setKpis] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [testando, setTestando] = useState(false);
  const [sincronizando, setSincronizando] = useState(false);
  const [togglingAuto, setTogglingAuto] = useState(false);
  const [msg, setMsg] = useState(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [cfg, kpiData, ticketsHoy] = await Promise.all([
        epelsaConfigService?.get(),
        epelsaKpisService?.getHoy(),
        epelsaTicketsLogService?.getHoy(),
      ]);
      setConfig(cfg);
      setKpis(kpiData);
      setTickets(ticketsHoy);
    } catch (e) {
      setMsg({ type: 'error', text: e?.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleTestConexion = async () => {
    if (!config?.id) return setMsg({ type: 'error', text: 'Configura primero la báscula en la pestaña Configuración Epelsa.' });
    setTestando(true);
    setMsg(null);
    try {
      // Prueba de conexión: actualizar estado a CONFIGURANDO y simular ping
      await epelsaConfigService?.setEstado(config?.id, 'CONFIGURANDO');
      await new Promise(r => setTimeout(r, 1200));
      // Sin protocolo real implementado aún — marcar como DESCONECTADA con aviso
      await epelsaConfigService?.setEstado(config?.id, 'DESCONECTADA');
      setMsg({ type: 'warn', text: 'Prueba de conexión completada. El protocolo físico aún no está configurado. Consulta la sección "Información necesaria para conectar" en Configuración Epelsa.' });
      await loadData();
    } catch (e) {
      setMsg({ type: 'error', text: `Error: ${e?.message}` });
    } finally {
      setTestando(false);
    }
  };

  const handleSincronizar = async () => {
    setSincronizando(true);
    setMsg(null);
    try {
      // Procesar ticket de demostración para validar el flujo completo
      const result = await epelsaTicketProcessor?.procesar(
        { ...TICKET_DEMO, numero_ticket: `EPELSA-TEST-${Date.now()}`, fecha: new Date()?.toISOString() },
        config?.id
      );
      if (result?.ok) {
        setMsg({ type: 'ok', text: `Sincronización completada. Venta creada: ${result?.ventaId?.substring(0, 8)}...` });
      } else if (result?.duplicado) {
        setMsg({ type: 'warn', text: 'Ticket duplicado detectado. No se ha creado una nueva venta.' });
      } else {
        setMsg({ type: 'error', text: `Errores: ${result?.errores?.join(', ')}` });
      }
      await loadData();
    } catch (e) {
      setMsg({ type: 'error', text: `Error: ${e?.message}` });
    } finally {
      setSincronizando(false);
    }
  };

  const handleToggleAuto = async () => {
    if (!config?.id) return;
    setTogglingAuto(true);
    try {
      const updated = await epelsaConfigService?.toggleRecepcionAutomatica(config?.id, !config?.recepcion_automatica);
      setConfig(updated);
      setMsg({ type: 'ok', text: `Recepción automática ${updated?.recepcion_automatica ? 'activada' : 'desactivada'}.` });
    } catch (e) {
      setMsg({ type: 'error', text: e?.message });
    } finally {
      setTogglingAuto(false);
    }
  };

  const estadoColor = config?.estado === 'CONECTADA' ? ACCENT : config?.estado === 'ERROR' ? '#F87171' : config?.estado === 'CONFIGURANDO' ? GOLD : 'rgba(255,255,255,0.35)';
  const estadoIcon = config?.estado === 'CONECTADA' ? 'Wifi' : config?.estado === 'ERROR' ? 'WifiOff' : config?.estado === 'CONFIGURANDO' ? 'Loader' : 'WifiOff';

  const msgStyle = msg?.type === 'ok'
    ? { bg: 'rgba(52,211,153,0.08)', border: 'rgba(52,211,153,0.25)', color: '#34D399', icon: 'CheckCircle' }
    : msg?.type === 'warn'
      ? { bg: 'rgba(251,191,36,0.08)', border: 'rgba(251,191,36,0.25)', color: '#FBBF24', icon: 'AlertTriangle' }
      : { bg: 'rgba(248,113,113,0.08)', border: 'rgba(248,113,113,0.25)', color: '#F87171', icon: 'AlertCircle' };

  return (
    <div className="space-y-5">
      {/* Mensaje de estado */}
      {msg && (
        <div className="flex items-start gap-3 p-4 rounded-xl" style={{ background: msgStyle?.bg, border: `1px solid ${msgStyle?.border}` }}>
          <Icon name={msgStyle?.icon} size={16} style={{ color: msgStyle?.color, flexShrink: 0, marginTop: 1 }} />
          <p className="font-oranienbaum text-sm" style={{ color: msgStyle?.color }}>{msg?.text}</p>
          <button onClick={() => setMsg(null)} className="ml-auto opacity-60 hover:opacity-100"><Icon name="X" size={14} style={{ color: msgStyle?.color }} /></button>
        </div>
      )}

      {/* Estado de conexión */}
      <div className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-oranienbaum font-semibold text-white text-base">Estado de Conexión</h3>
          <button onClick={loadData} className="p-1.5 rounded-lg text-white/30 hover:text-white/60 transition-colors" style={{ border: '1px solid rgba(255,255,255,0.08)' }}>
            <Icon name="RefreshCw" size={13} />
          </button>
        </div>
        {loading ? (
          <div className="flex items-center gap-3 py-4">
            <div className="w-5 h-5 rounded-full border-2 border-white/10 border-t-[#34D399] animate-spin" />
            <span className="font-oranienbaum text-white/40 text-sm">Cargando...</span>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Estado */}
            <div className="flex items-center gap-3 p-3 rounded-xl" style={{ background: `${estadoColor}10`, border: `1px solid ${estadoColor}25` }}>
              <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${estadoColor}15` }}>
                <Icon name={estadoIcon} size={17} style={{ color: estadoColor }} />
              </div>
              <div>
                <p className="font-oranienbaum text-xs text-white/40">Estado</p>
                <p className="font-oranienbaum font-semibold text-sm" style={{ color: estadoColor }}>{config?.estado || 'DESCONECTADA'}</p>
              </div>
            </div>
            {/* Báscula */}
            <div className="flex items-center gap-3 p-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
              <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.05)' }}>
                <Icon name="Scale" size={17} className="text-white/50" />
              </div>
              <div>
                <p className="font-oranienbaum text-xs text-white/40">Báscula</p>
                <p className="font-oranienbaum font-semibold text-white/80 text-sm">{config?.nombre || '—'}</p>
              </div>
            </div>
            {/* IP */}
            <div className="flex items-center gap-3 p-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
              <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.05)' }}>
                <Icon name="Network" size={17} className="text-white/50" />
              </div>
              <div>
                <p className="font-oranienbaum text-xs text-white/40">IP · Puerto</p>
                <p className="font-oranienbaum font-semibold text-white/80 text-sm">{config?.ip || '—'} · {config?.puerto || '—'}</p>
              </div>
            </div>
            {/* Última sincronización */}
            <div className="flex items-center gap-3 p-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
              <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.05)' }}>
                <Icon name="Clock" size={17} className="text-white/50" />
              </div>
              <div>
                <p className="font-oranienbaum text-xs text-white/40">Última sincronización</p>
                <p className="font-oranienbaum font-semibold text-white/80 text-sm">{fmtDate(config?.ultima_sincronizacion)}</p>
              </div>
            </div>
            {/* Último ticket */}
            <div className="flex items-center gap-3 p-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
              <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.05)' }}>
                <Icon name="Receipt" size={17} className="text-white/50" />
              </div>
              <div>
                <p className="font-oranienbaum text-xs text-white/40">Último ticket</p>
                <p className="font-oranienbaum font-semibold text-white/80 text-sm">{config?.ultimo_ticket_recibido || '—'}</p>
              </div>
            </div>
            {/* Ventas hoy */}
            <div className="flex items-center gap-3 p-3 rounded-xl" style={{ background: `${ACCENT}08`, border: `1px solid ${ACCENT}20` }}>
              <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${ACCENT}15` }}>
                <Icon name="TrendingUp" size={17} style={{ color: ACCENT }} />
              </div>
              <div>
                <p className="font-oranienbaum text-xs text-white/40">Ventas recibidas hoy</p>
                <p className="font-oranienbaum font-semibold text-sm" style={{ color: ACCENT }}>{fmtEur(kpis?.ventasHoy)} · {kpis?.ticketsProcesados ?? 0} tickets</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* KPIs rápidos */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Tickets hoy', value: kpis?.ticketsHoy ?? '—', icon: 'Receipt', color: '#60A5FA' },
          { label: 'Procesados', value: kpis?.ticketsProcesados ?? '—', icon: 'CheckCircle', color: ACCENT },
          { label: 'Duplicados', value: kpis?.ticketsDuplicados ?? '—', icon: 'Copy', color: GOLD },
          { label: 'Errores integración', value: kpis?.erroresIntegracion ?? '—', icon: 'AlertTriangle', color: '#F87171' },
        ]?.map((k, i) => (
          <div key={i} className="rounded-xl p-4 flex flex-col gap-2" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${k?.color}15`, border: `1px solid ${k?.color}25` }}>
              <Icon name={k?.icon} size={15} style={{ color: k?.color }} />
            </div>
            <p className="font-oranienbaum font-bold text-white text-xl">{loading ? '...' : k?.value}</p>
            <p className="font-oranienbaum text-white/45 text-xs">{k?.label}</p>
          </div>
        ))}
      </div>

      {/* Botones de acción */}
      <div className="flex flex-wrap gap-3">
        <button
          onClick={handleTestConexion}
          disabled={testando}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-oranienbaum font-semibold transition-all disabled:opacity-50"
          style={{ background: `${ACCENT}15`, border: `1px solid ${ACCENT}35`, color: ACCENT }}
        >
          {testando ? <div className="w-4 h-4 rounded-full border-2 border-[#34D399]/30 border-t-[#34D399] animate-spin" /> : <Icon name="Wifi" size={15} />}
          Probar conexión
        </button>
        <button
          onClick={handleSincronizar}
          disabled={sincronizando}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-oranienbaum font-semibold transition-all disabled:opacity-50"
          style={{ background: 'rgba(96,165,250,0.12)', border: '1px solid rgba(96,165,250,0.30)', color: '#60A5FA' }}
        >
          {sincronizando ? <div className="w-4 h-4 rounded-full border-2 border-blue-400/30 border-t-blue-400 animate-spin" /> : <Icon name="RefreshCw" size={15} />}
          Sincronizar ahora
        </button>
        <button
          onClick={handleToggleAuto}
          disabled={togglingAuto || !config?.id}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-oranienbaum font-semibold transition-all disabled:opacity-50"
          style={config?.recepcion_automatica
            ? { background: `${GOLD}12`, border: `1px solid ${GOLD}30`, color: GOLD }
            : { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.50)' }}
        >
          <Icon name={config?.recepcion_automatica ? 'ToggleRight' : 'ToggleLeft'} size={16} />
          Recepción automática: {config?.recepcion_automatica ? 'ON' : 'OFF'}
        </button>
      </div>

      {/* Tickets recibidos hoy */}
      <div className="rounded-2xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.07)' }}>
        <div className="px-4 py-3 flex items-center justify-between" style={{ background: 'rgba(255,255,255,0.04)', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
          <span className="font-oranienbaum text-white/60 text-sm font-semibold">Tickets recibidos hoy</span>
          <span className="font-oranienbaum text-white/30 text-xs">{tickets?.length} registros</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-max">
            <thead>
              <tr style={{ background: 'rgba(255,255,255,0.02)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                {['Nº Ticket', 'Caja', 'Hora', 'Estado', 'Venta vinculada']?.map(col => (
                  <th key={col} className="px-3 py-3 text-left">
                    <span className="font-oranienbaum text-white/40 text-xs uppercase tracking-wider">{col}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="px-4 py-10 text-center">
                  <div className="flex items-center justify-center gap-3">
                    <div className="w-5 h-5 rounded-full border-2 border-white/10 border-t-[#34D399] animate-spin" />
                    <span className="font-oranienbaum text-white/40 text-sm">Cargando...</span>
                  </div>
                </td></tr>
              ) : tickets?.length === 0 ? (
                <tr><td colSpan={5} className="px-4 py-12 text-center">
                  <p className="font-oranienbaum text-white/30 text-sm">No hay tickets recibidos hoy</p>
                </td></tr>
              ) : tickets?.map((t, i) => {
                const sc = t?.estado === 'PROCESADO' ? ACCENT : t?.estado === 'DUPLICADO' ? GOLD : t?.estado === 'PARCIAL' ? '#FBBF24' : '#F87171';
                return (
                  <tr key={t?.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)', background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)' }}>
                    <td className="px-3 py-3"><span className="font-oranienbaum text-white/80 text-sm">{t?.numero_ticket}</span></td>
                    <td className="px-3 py-3"><span className="font-oranienbaum text-white/50 text-xs">{t?.caja || '—'}</span></td>
                    <td className="px-3 py-3"><span className="font-oranienbaum text-white/50 text-xs">{fmtDate(t?.created_at)}</span></td>
                    <td className="px-3 py-3">
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-oranienbaum" style={{ background: `${sc}18`, color: sc, border: `1px solid ${sc}30` }}>{t?.estado}</span>
                    </td>
                    <td className="px-3 py-3"><span className="font-oranienbaum text-white/40 text-xs">{t?.venta_id ? t?.venta_id?.substring(0, 8) + '...' : '—'}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default EpelsaBascula;
